import type { PosCustomFeeCalc } from '@mlm/shared';
import type { FeeRow } from './cart-store';

/** A fee type as the payment method's registry lists it. */
type FeeType = { code: string; calc: PosCustomFeeCalc };

/**
 * The fee rows that will actually be charged, in the order they must be priced.
 *
 * DROPPED: a MANUAL row with no positive amount. An empty row the cashier added
 * and then thought better of must not become a ₱0 line on the tape, and its
 * empty string (or a half-typed ".") must never reach the Decimal fold, which
 * throws on it. A COMPUTED fee (percent / flat) has no typed amount to test, so
 * selecting it is the whole intent and it is always kept.
 *
 * SORTED into the method's registry order, not the on-screen row order. The
 * fold is a cascade (a percent fee charged after another fee is bigger) and the
 * server sorts this way before folding (`services/pos.ts`), so pricing the rows
 * in any other order quotes a total the server does not charge, and a card
 * tender for that total is rejected.
 */
export function chargeableFeeRows(fees: FeeRow[], feeTypes: FeeType[]): FeeRow[] {
  const rank = new Map(feeTypes.map((t, i) => [t.code, i] as const));
  const calcOf = (code: string) => feeTypes.find((t) => t.code === code)?.calc.kind ?? 'manual';
  return fees
    .filter((f) => (calcOf(f.typeCode) === 'manual' ? Number(f.amount) > 0 : true))
    .sort((a, b) => (rank.get(a.typeCode) ?? 0) - (rank.get(b.typeCode) ?? 0));
}
